import type { PosSaleStatus, PosSaleWithDetails, PosTenderKind } from '@erp/shared';
import { Banknote, Coins, CreditCard, Pencil, Receipt, RotateCcw, Wallet } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Badge, Button, confirmDialog, Skeleton } from '../../components/ui/index.js';
import { usePermission } from '../../hooks/usePermission.js';
import { api } from '../../lib/api.js';
import { formatDateTime } from '../../lib/intl.js';
import { formatMoney, trimMoney } from '../../lib/money.js';

/** Shared status→tone map — one POS sale can be read from the live register
 *  (this shift) or the admin list (every terminal); both render it the same way. */
export const SALE_STATUS_TONE: Record<PosSaleStatus, 'neutral' | 'success' | 'warning' | 'danger' | 'info'> = {
  parked: 'neutral',
  finalized: 'success',
  voided: 'danger',
  refunded: 'warning',
  partially_refunded: 'warning',
};

const TENDER_ICON: Record<PosTenderKind, typeof Banknote> = {
  cash: Banknote,
  card: CreditCard,
  wallet: Wallet,
  cashback: Coins,
  other: Coins,
};

/**
 * Fetches one sale's full detail (lines + tenders — the list endpoint carries
 * neither) on demand. Pass `null` to stay idle, e.g. while a master list is
 * showing and nothing is selected yet.
 */
export function usePosSaleDetail(saleId: string | null): {
  detail: PosSaleWithDetails | null;
  loading: boolean;
} {
  const [detail, setDetail] = useState<PosSaleWithDetails | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!saleId) {
      setDetail(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setDetail(null);
    (async () => {
      try {
        const r = await api().get<PosSaleWithDetails>(`/pos/sales/${saleId}`);
        if (!cancelled) setDetail(r.data);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [saleId]);

  return { detail, loading };
}

function Row({ label, value, bold }: { label: ReactNode; value: ReactNode; bold?: boolean }): JSX.Element {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className={bold ? 'font-semibold' : 'text-fg-muted'}>{label}</span>
      <span className={bold ? 'font-mono text-base font-bold' : 'font-mono'}>{value}</span>
    </div>
  );
}

function Meta({ label, value }: { label: ReactNode; value: ReactNode }): JSX.Element {
  return (
    <div className="min-w-0">
      <div className="text-xs text-fg-muted">{label}</div>
      <div className="truncate text-sm font-medium">{value}</div>
    </div>
  );
}

/** Loading placeholder — same shape whichever dialog embeds this. */
export function PosSaleDetailSkeleton(): JSX.Element {
  return (
    <div className="flex flex-col gap-3">
      <Skeleton className="h-6 w-40" />
      <Skeleton className="h-16 w-full" />
      <Skeleton className="h-24 w-full" />
      <Skeleton className="h-16 w-full" />
    </div>
  );
}

/**
 * Full read-only view of one POS sale — line items, payment breakdown, and
 * totals — the "more details" a sale-number/status glance can't show. Pure
 * render (no fetching) so both the live register's "this shift" dialog and
 * the admin `/pos/sales` list can embed it with their own party/cashier/
 * terminal lookups (register knows its own cashier+terminal already; the
 * admin list resolves them from directories it loads once).
 */
export function PosSaleDetailBody({
  detail,
  partyName,
  cashierName,
  terminalName,
  onOpenInvoice,
  onRestored,
  onEdit,
  editing,
}: {
  detail: PosSaleWithDetails;
  partyName?: string;
  cashierName?: string;
  terminalName?: string;
  onOpenInvoice?: () => void;
  /** Called after a voided sale is put back, so the host can refresh. */
  onRestored?: () => void;
  /** Offered on a FINISHED sale to whoever may edit it (`pos.sale.edit`): re-open it as a cart. */
  onEdit?: () => void;
  editing?: boolean;
}): JSX.Element {
  const { t, i18n } = useTranslation('pos');
  const { t: tc } = useTranslation();
  const { sale, lines, tenders } = detail;
  const m = (v: string): string => formatMoney(v, { locale: i18n.language, currency: sale.currencyCode });
  const canRefund = usePermission('pos.refund');
  const [restoring, setRestoring] = useState(false);

  /**
   * Undo a void. The server re-posts the reversed entries as NEW ones (GL
   * history is never rewritten), takes the stock back off the shelf, gives the
   * customer their cashback back and re-issues the invoice — and refuses
   * outright if the shift has been closed or the stock is no longer there.
   */
  const restore = async (allowNegativeStock = false): Promise<void> => {
    if (!allowNegativeStock) {
      const ok = await confirmDialog({
        title: t('sales.detail.restoreTitle', { defaultValue: 'Put this sale back?' }),
        message: t('sales.detail.restoreBody', {
          sale: sale.saleNumber,
          defaultValue: 'Sale {{sale}} goes back to finalized: the stock leaves the shelf again, the customer owes it again, and their cashback is returned. The void stays in the ledger as a record.',
        }),
      });
      if (!ok) return;
    }
    setRestoring(true);
    try {
      await api().post(`/pos/sales/${sale.id}/unvoid`, {
        reason: t('sales.detail.restoreReason', { defaultValue: 'Void undone' }),
        ...(allowNegativeStock ? { allowNegativeStock: true } : {}),
      });
      toast.success(t('sales.detail.restored', { sale: sale.saleNumber, defaultValue: 'Sale {{sale}} restored.' }));
      onRestored?.();
    } catch (err) {
      const cause = (err as { response?: { data?: { cause?: { code?: string; itemName?: string; available?: string; required?: string } } } }).response?.data?.cause;
      if (cause?.code === 'session_closed') {
        toast.error(t('sales.detail.restoreShiftClosed', { defaultValue: 'That shift has been closed and counted — a sale cannot be put back into it.' }));
        return;
      }
      if (cause?.code === 'restore_stock_unavailable') {
        // The goods have been sold since the void. Offer the override rather
        // than a dead end: if the sale really happened the goods really left,
        // and it is the STOCK figure that is wrong — the void received back
        // what never came back — so negative is the honest number.
        const go = await confirmDialog({
          title: t('sales.detail.restoreNoStockTitle', { defaultValue: 'Those goods have been sold since' }),
          message: t('sales.detail.restoreNoStockBody', {
            item: cause.itemName ?? '',
            required: cause.required ?? '',
            available: cause.available ?? '',
            defaultValue: '{{item}} needs {{required}} taken back out and only {{available}} is on hand. If this sale really happened, the goods really left and the stock count is what is wrong — restoring will take it negative. Count that item afterwards.',
          }),
          tone: 'danger',
          confirmLabel: t('sales.detail.restoreAnyway', { defaultValue: 'Restore anyway' }),
        });
        if (go) await restore(true);
        return;
      }
      toast.error(tc('errors:server'));
    } finally {
      setRestoring(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <div className="font-mono text-sm font-semibold">{sale.saleNumber}</div>
          <div className="text-xs text-fg-muted">
            {formatDateTime(sale.saleDate, { locale: i18n.language }, { dateStyle: 'medium', timeStyle: 'short' })}
          </div>
        </div>
        <Badge tone={SALE_STATUS_TONE[sale.status]}>{t(`sales.status.${sale.status}`)}</Badge>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Meta label={t('sales.table.customer', { defaultValue: 'Customer' })} value={partyName ?? '—'} />
        {cashierName && <Meta label={t('shift.cashier')} value={cashierName} />}
        {terminalName && <Meta label={t('sales.table.terminal')} value={terminalName} />}
      </div>

      {sale.couponCode && (
        <div className="text-xs text-fg-muted">
          {t('sales.detail.coupon', { defaultValue: 'Coupon' })}:{' '}
          <span dir="ltr" className="font-mono text-fg">{sale.couponCode}</span>
        </div>
      )}

      <div className="flex flex-col divide-y divide-border rounded-md border border-border">
        {lines.map((l) => (
          <div key={l.id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
            <div className="min-w-0">
              <div className="truncate font-medium">{l.description ?? '—'}</div>
              <div className="text-xs text-fg-muted">
                {trimMoney(l.quantity)} × {m(l.unitPrice)}
                {l.lineDiscountKind !== 'none' &&
                  ` · −${l.lineDiscountKind === 'percent' ? `${trimMoney(l.lineDiscountValue)}%` : m(l.lineDiscountValue)}`}
              </div>
            </div>
            <div className="shrink-0 font-mono text-sm font-semibold">{m(l.lineTotal)}</div>
          </div>
        ))}
      </div>

      <div className="flex flex-col gap-1.5 rounded-md bg-bg-subtle/60 px-3 py-2.5 text-sm">
        <Row label={t('sale.subtotal')} value={m(sale.subtotal)} />
        {Number(sale.discountTotal) > 0 && <Row label={t('sale.discount')} value={`− ${m(sale.discountTotal)}`} />}
        {Number(sale.taxTotal) > 0 && <Row label={t('sale.tax')} value={m(sale.taxTotal)} />}
        <Row label={t('sale.total')} value={m(sale.total)} bold />
        {Number(sale.tenderTotal) > 0 && <Row label={t('sale.tenderTotal')} value={m(sale.tenderTotal)} />}
        {Number(sale.changeDue) > 0 && <Row label={t('sale.changeDue')} value={m(sale.changeDue)} />}
      </div>

      {tenders.length > 0 && (
        <div className="flex flex-col gap-2">
          <div className="text-xs font-medium uppercase tracking-wide text-fg-muted">{t('tender.tendersHeading')}</div>
          <div className="flex flex-col gap-1.5">
            {tenders.map((tnd) => {
              const Icon = TENDER_ICON[tnd.kind];
              return (
                <div key={tnd.id} className="flex items-center justify-between gap-3 rounded-md border border-border px-3 py-2 text-sm">
                  <div className="flex min-w-0 items-center gap-2">
                    <Icon className="h-4 w-4 shrink-0 text-fg-muted" aria-hidden />
                    <span>{t(`tender.${tnd.kind}`)}</span>
                    {tnd.reference && <span className="truncate text-xs text-fg-muted">· {tnd.reference}</span>}
                    {tnd.cardLast4 && (
                      <span className="text-xs text-fg-muted" dir="ltr">
                        •••• {tnd.cardLast4}
                      </span>
                    )}
                  </div>
                  <span className="shrink-0 font-mono font-semibold">{m(tnd.amount)}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {sale.status === 'voided' && (sale.voidReason || sale.voidedAt) && (
        <div className="rounded-md border border-danger/30 bg-danger/5 px-3 py-2 text-xs text-danger">
          {sale.voidReason && (
            <div>
              {t('sales.detail.voidReason', { defaultValue: 'Void reason' })}: {sale.voidReason}
            </div>
          )}
          {sale.voidedAt && (
            <div>
              {t('sales.detail.voidedAt', { defaultValue: 'Voided at' })}:{' '}
              {formatDateTime(sale.voidedAt, { locale: i18n.language }, { dateStyle: 'medium', timeStyle: 'short' })}
            </div>
          )}
          {/* Put it back. Offered only to whoever may refund at the till,
              because restoring moves money and stock exactly as the void did,
              only the other way. The server refuses if the shift has since
              been closed or the goods have since been sold. */}
          {canRefund && (
            <div className="mt-2">
              <Button variant="secondary" size="sm" loading={restoring} onClick={() => void restore(false)}>
                <RotateCcw className="h-4 w-4" aria-hidden />
                {t('sales.detail.restore', { defaultValue: 'Restore this sale' })}
              </Button>
            </div>
          )}
        </div>
      )}

      {(sale.invoiceId && onOpenInvoice) || (sale.status === 'finalized' && onEdit) ? (
        <div className="flex flex-wrap gap-2">
          {sale.invoiceId && onOpenInvoice && (
            <Button variant="secondary" size="sm" onClick={onOpenInvoice}>
              <Receipt className="h-4 w-4" aria-hidden />
              {t('sales.openInvoice')}
            </Button>
          )}
          {sale.status === 'finalized' && onEdit && (
            <Button variant="secondary" size="sm" loading={editing} onClick={onEdit}>
              <Pencil className="h-4 w-4" aria-hidden />
              {t('sales.detail.edit', { defaultValue: 'Edit this sale' })}
            </Button>
          )}
        </div>
      ) : null}
    </div>
  );
}
